<script setup lang="ts">
import type { PaymentModelAlert } from '~/ui-blocks/Formalization/blocks/paymentModel/paymentModel.types'
import { SolAlert } from '@solfacil/girassol'
import { I18nT } from 'vue-i18n'
import { PaymentModelStatus } from '~/constants/receiptModel'
import { CORRECTION_LETTER_FIELD_ID } from '~/ui-blocks/Formalization/blocks/paymentModel/paymentModel.constants'

const {
  hasComboOrMirror,
  showDeadlineAlert,
  deadlineAlertTitle,
  stepStatus,
  invoicesToShow,
  hasInvoiceDenied,
  usesInvoiceDeniedAlert,
  alertError,
  alertSuccess,
} = defineProps<{
  hasComboOrMirror: boolean
  showDeadlineAlert: boolean
  deadlineAlertTitle: string
  stepStatus: string | null
  invoicesToShow: boolean
  hasInvoiceDenied: boolean
  usesInvoiceDeniedAlert: boolean
  alertError: PaymentModelAlert
  alertSuccess: PaymentModelAlert
}>()

const { t } = useI18n()

const FOCUSABLE_SELECTOR = 'input:not([disabled]):not([type="hidden"]), button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'

function goToCorrectionLetterField() {
  const field = document.getElementById(CORRECTION_LETTER_FIELD_ID)
  if (!field)
    return

  field.scrollIntoView({ behavior: 'smooth', block: 'center' })

  const focusable = field.matches(FOCUSABLE_SELECTOR)
    ? field
    : field.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)

  focusable?.focus({ preventScroll: true })
  if (focusable && document.activeElement === focusable)
    return

  if (!field.hasAttribute('tabindex'))
    field.setAttribute('tabindex', '-1')
  field.focus({ preventScroll: true })
}
</script>

<template>
  <div class="mt-2 receipt-model__subtitle">
    <p v-if="hasComboOrMirror">
      {{ t('customer.receipt_model_subtitle_combo') }}
    </p>
    <template v-else>
      <p>{{ t('customer.receipt_model_subtitle_default') }}</p>
      <p>{{ t('customer.receipt_model_subtitle_default_complement') }}</p>
    </template>
  </div>

  <SolAlert
    v-if="showDeadlineAlert"
    id="invoice-deadline-alert"
    feedback="warning"
    :title="deadlineAlertTitle"
  >
    {{ t('customer.invoice_deadline_alert.text') }}
  </SolAlert>

  <template v-if="hasInvoiceDenied || alertSuccess.title">
    <template v-if="alertError.title && invoicesToShow && stepStatus === PaymentModelStatus.TO_REVIEW">
      <SolAlert
        v-if="usesInvoiceDeniedAlert"
        :id="`${stepStatus}-alert-error`"
        feedback="error"
        :title="t('receipt-model.layout.invoice_denied_alert.title')"
      >
        <span class="block">
          <I18nT keypath="receipt-model.layout.invoice_denied_alert.fix_text" tag="span">
            <template #correctionLetter>
              <strong>{{ t('receipt-model.layout.invoice_denied_alert.correction_letter') }}</strong>
            </template>
          </I18nT>
        </span>
        <span class="block">
          <I18nT keypath="receipt-model.layout.invoice_denied_alert.attach_text" tag="span">
            <template #attachLink>
              <button
                id="go-to-correction-letter"
                type="button"
                class="receipt-model__inline-link"
                @click="goToCorrectionLetterField"
              >
                {{ t('receipt-model.layout.invoice_denied_alert.attach_link') }}
              </button>
            </template>
          </I18nT>
        </span>
      </SolAlert>

      <SolAlert
        v-else
        :id="`${stepStatus}-alert-error`"
        :feedback="alertError.type"
        :title="alertError.title"
      >
        {{ alertError.description }}
      </SolAlert>
    </template>

    <SolAlert
      v-if="alertSuccess.title"
      :id="`${stepStatus}-alert-success`"
      :feedback="alertSuccess.type"
      :title="alertSuccess.title"
    >
      {{ alertSuccess.description }}
    </SolAlert>
  </template>
</template>
